/**
 * Satellite-imagery settings: the optional premium key (MapTiler), the overlay
 * on/off toggle, the imagery source choice (free Esri vs premium MapTiler HD),
 * the subtle-dim toggle for legibility, and the blend opacity. Kept on the device
 * in localStorage, mirroring the AIS-key pattern; the key overrides the
 * .env.local value and is never committed to source.
 */
import { create } from 'zustand';
import type { SatelliteSourceId } from '@/map/satelliteBasemap';

const KEY_LS = 'acp.satellite.key.v1';
const DIM_LS = 'acp.satellite.dim.v1';
const ON_LS = 'acp.satellite.on.v1';
const SRC_LS = 'acp.satellite.source.v1';
const OPACITY_LS = 'acp.satellite.opacity.v1';

function loadKey(): string {
  try {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(KEY_LS) : null) ?? '';
  } catch {
    return '';
  }
}
function loadDim(): boolean {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(DIM_LS) : null;
    return v == null ? true : v === '1';
  } catch {
    return true;
  }
}
function loadEnabled(): boolean {
  try {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(ON_LS) : null) === '1';
  } catch {
    return false;
  }
}
function loadSourceId(): SatelliteSourceId {
  try {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(SRC_LS) : null) === 'maptiler'
      ? 'maptiler'
      : 'esri';
  } catch {
    return 'esri';
  }
}
function loadOpacity(): number {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(OPACITY_LS) : null;
    const n = v == null ? 1 : parseFloat(v);
    return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 1;
  } catch {
    return 1;
  }
}
function save(k: string, v: string): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
}

interface SatelliteState {
  /** Premium (MapTiler) key from Settings; overrides VITE_MAPTILER_KEY. */
  apiKey: string;
  setApiKey: (k: string) => void;
  /** The satellite OVERLAY toggle. On = imagery shows under (vector) or instead of (raster) the chart. */
  enabled: boolean;
  setEnabled: (b: boolean) => void;
  /** Which imagery source: free Esri or premium MapTiler HD. */
  sourceId: SatelliteSourceId;
  setSourceId: (id: SatelliteSourceId) => void;
  /** Subtle dim/desaturate of the imagery so marks stay legible (default on). */
  dim: boolean;
  setDim: (b: boolean) => void;
  /** Imagery layer opacity 0..1 (the blend strength under a vector chart). */
  opacity: number;
  setOpacity: (n: number) => void;
}

export const useSatelliteStore = create<SatelliteState>((set) => ({
  apiKey: loadKey(),
  setApiKey: (apiKey) => {
    save(KEY_LS, apiKey);
    set({ apiKey });
  },
  enabled: loadEnabled(),
  setEnabled: (enabled) => {
    save(ON_LS, enabled ? '1' : '0');
    set({ enabled });
  },
  sourceId: loadSourceId(),
  setSourceId: (sourceId) => {
    save(SRC_LS, sourceId);
    set({ sourceId });
  },
  dim: loadDim(),
  setDim: (dim) => {
    save(DIM_LS, dim ? '1' : '0');
    set({ dim });
  },
  opacity: loadOpacity(),
  setOpacity: (opacity) => {
    const o = Math.min(1, Math.max(0, opacity));
    save(OPACITY_LS, String(o));
    set({ opacity: o });
  },
}));
